import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  AggregatesDirective,
  AggregateDirective,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  Inject,
  Aggregate,
} from '@syncfusion/ej2-react-grids';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '@/store';
import {
  getDashboardMetric22,
  type DashboardMetric22CycleTestingStatus,
} from '@/services/dashboardService';

const GridWrapper = styled.div`
  /* Ensure consistent sizing across header/body/footer */
  .e-grid *,
  .e-grid *::before,
  .e-grid *::after {
    box-sizing: border-box;
  }

  /* Make footer (aggregate) cells match row cell padding/borders */
  .e-grid .e-summaryrow .e-summarycell,
  .e-grid .e-summaryrow .e-summarycontent,
  .e-grid .e-summarycell {
    padding: 12px 16px !important;
    border-color: #e5e7eb !important;
    vertical-align: middle;
  }

  .e-grid .e-summaryrow .e-summarycell {
    font-size: 14px;
    color: #1f2937;
  }

  .weight-warning {
    color: #cf1322;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .weight-ok {
    color: #389e0d;
    font-weight: 600;
  }
`;

export type EX1CycleTestingStatusGridProps = {
  title?: string;
  minHeight?: number;
};

const formatPercent = (value: number | null | undefined) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
  // If API ever returns 0..1, this keeps it safe:  <--- Removed, as this caused testing statuses < 1 to show up as much larger percentages...
  const pct = value; //value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
};

const averageFieldWithCycleWeight = (rows: any[]) => {
  const nums = (rows || [])
    .map(r => r?.isInTestingScope ? (r?.["testingStatus"] * r?.cycleWeight) : 0)
    .filter(v => typeof v === 'number' && !Number.isNaN(v));

  const cycles = (rows || [])
    .map(r => r?.isInTestingScope ? r?.["cycleWeight"] : 0)
    .filter(v => typeof v === 'number' && !Number.isNaN(v));

  const cycleWeights = cycles.reduce((a, b) => a + b, 0);

  if (cycleWeights === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / (cycleWeights);
};

const sumField = (rows: any[], field: string) => {
  const nums = (rows || [])
    .map(r => r?.[field])
    .filter(v => typeof v === 'number' && !Number.isNaN(v));

  return nums.reduce((a, b) => a + b, 0);
};

export const EX1CycleTestingStatusGrid: React.FC<
  EX1CycleTestingStatusGridProps
> = ({
  title = 'Cycle Testing Completion Status',
  minHeight = 220,
}) => {
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [rows, setRows] = useState<DashboardMetric22CycleTestingStatus[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRows([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric22(annualReportId);
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRows([]);
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Metric 13.',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const dataSource = useMemo(() => {
    // Keep a stable key for the grid
    return rows.map(r => ({
      ...r,
      id: r.cycleId,
      cycleName: r.cycleCode + " " + r.cycleName
    }));
  }, [rows]);

  const labelAggregate = (_props: any): string => 'Totals';

  const testingStatusAggregate = (props: any): number =>
    averageFieldWithCycleWeight(props?.result || []);

  const gapsAggregate = (props: any): number =>
    sumField(props?.result || [], 'gapsNumber');

  const hoursAggregate = (props: any): number =>
    sumField(props?.result || [], 'auditorHours');

  const weeksAggregate = (props: any): number =>
    sumField(props?.result || [], 'auditorWeeks');

  const samplesAggregate = (props: any): number =>
    sumField(props?.result || [], 'totalSamplesCount');

  const footerLabelTemplate = (_props: any) => (
    <div style={{ fontWeight: 600 }}>Totals</div>
  );

  const footerPercentTemplate = (props: any) => (
    <div style={{ fontWeight: 600 }}>{formatPercent(props?.Custom)}</div>
  );

  const footerSumTemplate = (props: any) => (
    <div style={{ fontWeight: 600 }}>{props?.Custom ?? 0}</div>
  );

  return (
    <Card title={title}>
      {loading ? (
        <div
          style={{
            minHeight, // ✅ uses min height only for loading
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : errorMessage ? (
        <div style={{ padding: 16 }}>
          <Alert
            type='error'
            message='Dashboard Metric 22 Error'
            description={errorMessage}
            showIcon
          />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <GridWrapper>
            <GridComponent
              dataSource={dataSource}
              height='auto' // ✅ key change
              width='100%' // ✅ key change
              allowSorting={false}
              allowFiltering={false}
              enableHover={true}
              rowHeight={44}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='cycleName'
                  headerText='Cycle Name'
                  width={200}
                  textAlign='Left'
                  clipMode='EllipsisWithTooltip'
                />
                <ColumnDirective
                  field='testingStatus'
                  headerText='Testing Status'
                  width={120}
                  textAlign='Center'
                  template={(row: any) =>
                    formatPercent( row?.isInTestingScope ? row?.testingStatus : "N/A")
                  }
                />
                <ColumnDirective
                  field='gapsNumber'
                  headerText='GAPs #'
                  width={90}
                  textAlign='Center'
                />
                <ColumnDirective
                  field='auditorHours'
                  headerText='Budget Hours'
                  width={120}
                  textAlign='Center'
                  template={(row: any) => {
                    const value = row?.isInTestingScope ? row?.auditorHours : "N/A";
                    if (typeof value !== 'number') return 'N/A';
                    return value;
                  }}
                />
                <ColumnDirective
                  field='auditorWeeks'
                  headerText='Auditor Weeks'
                  width={120}
                  textAlign='Center'
                  template={(row: any) => {
                    const value = row?.isInTestingScope ? row?.auditorWeeks : "N/A";
                    if (typeof value !== 'number') return 'N/A';
                    return value;
                  }}
                />
                <ColumnDirective
                  field='processWeightSum'
                  headerText='Process Weight'
                  width={140}
                  textAlign='Center'
                  template={(row: any) => {
                    const weight = row?.isInTestingScope ? row?.processWeightSum : "N/A";
                    if (typeof weight !== 'number') return 'N/A';
                    if (weight !== 100) {
                      return (
                        <span className='weight-warning'>
                          <WarningOutlined /> {weight}%
                        </span>
                      );
                    }
                    return <span className='weight-ok'>{weight}%</span>;
                  }}
                />
                <ColumnDirective
                  field='totalSamplesCount'
                  headerText='Total Samples'
                  width={120}
                  textAlign='Center'
                  template={(row: any) => {
                    const value = row?.isInTestingScope ? row?.totalSamplesCount : "N/A";
                    if (typeof value !== 'number') return 'N/A';
                    return value;
                  }}
                />
              </ColumnsDirective>

              <AggregatesDirective>
                <AggregateDirective>
                  <AggregateColumnsDirective>
                    <AggregateColumnDirective
                      field='cycleName'
                      type='Custom'
                      customAggregate={labelAggregate}
                      footerTemplate={footerLabelTemplate}
                    />
                    <AggregateColumnDirective
                      field='testingStatus'
                      type='Custom'
                      customAggregate={testingStatusAggregate}
                      footerTemplate={footerPercentTemplate}
                    />
                    <AggregateColumnDirective
                      field='gapsNumber'
                      type='Custom'
                      customAggregate={gapsAggregate}
                      footerTemplate={footerSumTemplate}
                    />
                    <AggregateColumnDirective
                      field='auditorHours'
                      type='Custom'
                      customAggregate={hoursAggregate}
                      footerTemplate={footerSumTemplate}
                    />
                    <AggregateColumnDirective
                      field='auditorWeeks'
                      type='Custom'
                      customAggregate={weeksAggregate}
                      footerTemplate={footerSumTemplate}
                    />
                    <AggregateColumnDirective
                      field='totalSamplesCount'
                      type='Custom'
                      customAggregate={samplesAggregate}
                      footerTemplate={footerSumTemplate}
                    />
                  </AggregateColumnsDirective>
                </AggregateDirective>
              </AggregatesDirective>

              <Inject services={[Aggregate]} />
            </GridComponent>
          </GridWrapper>
        </div>
      )}
    </Card>
  );
};

export default EX1CycleTestingStatusGrid;
